type NestedOptionLike = {
  question?: string | null;
  questionIndex?: number | null;
  order?: number | null;
};

type NestedGroup = {
  question: string;
  optionKeys: string[];
  minOrder: number;
  sequence: number;
};

export type NestedQuestionTranslationEntry = {
  key: string;
  question: string;
  questionIndex: number;
  optionKeys: string[];
};

function normalizeQuestionText(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function normalizeQuestionIndex(value: unknown): number | null {
  if (!Number.isInteger(value) || Number(value) < 0) {
    return null;
  }
  return Number(value);
}

function normalizeOrder(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return value;
}

export function genNestedQuestionLabelKey(questionKey: string, questionIndex: number): string {
  const safeIndex = Number.isInteger(questionIndex) && questionIndex >= 0 ? questionIndex : 0;
  return `${questionKey}-nested-question-${safeIndex}-label-key`;
}

export function collectNestedQuestionTranslationEntries(params: {
  questionKey?: string | null;
  answerOptionsMap?: Record<string, NestedOptionLike | undefined> | null;
}): NestedQuestionTranslationEntry[] {
  const { questionKey, answerOptionsMap } = params;
  if (!questionKey || !answerOptionsMap) {
    return [];
  }

  const explicitGroups = new Map<number, NestedGroup>();
  const implicitGroups = new Map<string, NestedGroup>();
  let sequence = 0;

  Object.entries(answerOptionsMap).forEach(([optionKey, optionRaw]) => {
    const questionText = normalizeQuestionText(optionRaw?.question);
    if (!questionText) {
      return;
    }
    const questionIndex = normalizeQuestionIndex(optionRaw?.questionIndex);
    const order = normalizeOrder(optionRaw?.order);
    sequence += 1;

    if (questionIndex !== null) {
      const existing = explicitGroups.get(questionIndex);
      if (!existing) {
        explicitGroups.set(questionIndex, {
          question: questionText,
          optionKeys: [optionKey],
          minOrder: order,
          sequence,
        });
      } else {
        existing.optionKeys.push(optionKey);
        existing.minOrder = Math.min(existing.minOrder, order);
      }
      return;
    }

    const implicitKey = questionText;
    const existing = implicitGroups.get(implicitKey);
    if (!existing) {
      implicitGroups.set(implicitKey, {
        question: questionText,
        optionKeys: [optionKey],
        minOrder: order,
        sequence,
      });
      return;
    }

    existing.optionKeys.push(optionKey);
    existing.minOrder = Math.min(existing.minOrder, order);
  });

  const usedIndices = new Set<number>();
  const entries: Array<{ questionIndex: number; question: string; optionKeys: string[] }> = [];

  Array.from(explicitGroups.entries())
    .sort((a, b) => a[0] - b[0])
    .forEach(([index, group]) => {
      usedIndices.add(index);
      entries.push({
        questionIndex: index,
        question: group.question,
        optionKeys: [...group.optionKeys],
      });
    });

  let nextIndex = 0;
  Array.from(implicitGroups.values())
    .sort((a, b) => {
      if (a.minOrder !== b.minOrder) {
        return a.minOrder - b.minOrder;
      }
      return a.sequence - b.sequence;
    })
    .forEach((group) => {
      while (usedIndices.has(nextIndex)) {
        nextIndex += 1;
      }
      usedIndices.add(nextIndex);
      entries.push({
        questionIndex: nextIndex,
        question: group.question,
        optionKeys: [...group.optionKeys],
      });
      nextIndex += 1;
    });

  return entries
    .sort((a, b) => a.questionIndex - b.questionIndex)
    .map((entry) => ({
      key: genNestedQuestionLabelKey(questionKey, entry.questionIndex),
      question: entry.question,
      questionIndex: entry.questionIndex,
      optionKeys: entry.optionKeys,
    }));
}
